import { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Users, 
  FileText, 
  CreditCard, 
  Award,
  Phone as PhoneIcon,
  MessageCircle,
  ZoomIn
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '../contexts/LanguageContext'
import { InternationalizedText, getLocalizedText, getLocalizedNumber } from '../hooks/useTours'
import { FullScreenImageModal } from '../components/TourDetails/FullScreenImageModal'

// Helper function to format phone number for display
function formatPhoneNumber(phone: string, language: string): string {
  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '')
  // Format: +92-XXX-XXXXXXX
  let formatted = phone
  if (cleaned.startsWith('+92')) {
    const rest = cleaned.substring(3)
    if (rest.length >= 3) {
      formatted = `+92-${rest.substring(0, 3)}-${rest.substring(3)}`
    }
  }
  // Apply localized numbers
  return getLocalizedNumber(formatted, language)
}

// Helper function to get clean phone number for tel: and WhatsApp links
function getCleanPhoneNumber(phone: string): string {
  return phone.replace(/[^\d+]/g, '')
}

// Phone number component with call and WhatsApp icons
function PhoneNumberDisplay({ phone, className = "" }: { phone: string; className?: string }) {
  const { isRTL, language } = useLanguage()
  const formattedPhone = formatPhoneNumber(phone, language)
  const cleanPhone = getCleanPhoneNumber(phone)
  const whatsappUrl = `https://wa.me/${cleanPhone.replace('+', '')}`
  const telUrl = `tel:${cleanPhone}`

  return (
    <div className={`flex items-center gap-2 w-full ${isRTL ? 'flex-row-reverse' : ''} ${className}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <span className={`phone flex-1 ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'} style={isRTL ? { textAlign: 'right' } : { textAlign: 'left' }}>{formattedPhone}</span>
      <div className={`flex items-center gap-1 flex-shrink-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <a
          href={telUrl}
          className="p-1.5 rounded-full bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
          aria-label="Call"
          title="Call"
        >
          <PhoneIcon className="h-4 w-4" />
        </a>
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1.5 rounded-full bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
          aria-label="WhatsApp"
          title="WhatsApp"
        >
          <MessageCircle className="h-4 w-4" />
        </a>
      </div>
    </div>
  )
}

export function Registration() {
  const { t } = useTranslation()
  const { isRTL, direction, language } = useLanguage()
  const [selectedCertificateImage, setSelectedCertificateImage] = useState<string | null>(null)
  const [selectedClearanceImage, setSelectedClearanceImage] = useState<string | null>(null)
  const [selectedOtherCertificateImage, setSelectedOtherCertificateImage] = useState<string | null>(null)
  // Dummy data with internationalized content
  const registrationData = {
    registration: {
      certificateNumber: "-",
      ministry: "Ministry of Religious Affairs & Interfaith Harmony, Islamabad",
      validityDate: "-",
      expiryDate: "-"
    },
    business: {
      legalName: "Karwan e Karbala o Sham (Pvt) Ltd",
      establishmentDate: "2020-03-15",
      registrationNumber: "SECP-2020-567890",
      registrationValidity: "2025-03-14",
      officeAddress: {
        en: "Office # 10, Karbala Gamysha, Lower Mall Lahore, Pakistan 54000",
        ur: "دفتر نمبر 10، کربلا گمیشا، لوئر مال لاہور، پاکستان 54000",
        ar: "المكتب رقم 10، كربلاء جميشا، المول السفلي لاهور، باكستان 54000",
        fa: "دفتر شماره 10، کربلا گمیشا، مال پایین لاهور، پاکستان 54000"
      } as InternationalizedText,
      website: "https://www.karwanekarbalaosham.net",
      phone: "+92-334-9985065",
      email: "karwanekarbalaosham@gmail.com",
      bankAccounts: [
        {
          bank: {
            en: "Habib Bank Limited",
            ur: "حبیب بینک لمیٹڈ",
            ar: "بنك حبيب المحدود",
            fa: "بانک حبیب محدود"
          } as InternationalizedText,
          title: {
            en: "Karwan e Karbal",
            ur: "کاروان کربلا",
            ar: "قافلة كربلاء",
            fa: "کاروان کربلا"
          } as InternationalizedText,
          accountNumber: "15187900953703",
          iban: "PK08HABB0015187900953703",
          branch: {
            en: "-",
            ur: "-",
            ar: "-",
            fa: "-"
          } as InternationalizedText
        }
      ]
    },
    clearance: {
      hostCountry: {
        en: "-",
        ur: "-",
        ar: "-",
        fa: "-"
      } as InternationalizedText,
      certificateNumber: "-",
      issueDate: "-",
      expiryDate: "-",
      status: "Valid"
    }
  }

  return (
    <div className="pt-16" dir={direction}>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-primary/10 to-primary/5">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold text-foreground mb-6"
            dir={direction}
          >
            {t('registration.title')}
            <span className="text-primary block">{t('registration.subtitle')}</span>
          </motion.h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 space-y-12">
          
          {/* Registration Certificate */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-card border border-border rounded-2xl p-8"
            dir={direction}
          >
            <div className={`flex items-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="p-3 bg-primary/10 rounded-lg">
                <Award className="h-6 w-6 text-primary" />
              </div>
              <h2 className="text-3xl font-bold text-card-foreground" dir={direction}>
                {t('registration.certificate.title')}
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 bg-secondary/50 rounded-xl border border-border" dir={direction}>
                <h3 className={`font-semibold text-card-foreground mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.certificate.details')}</h3>
                <div className="space-y-3">
                  <div>
                    <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.certificate.certificateNumber')}</p>
                    <p className={`text-card-foreground font-medium phone ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>{getLocalizedNumber(registrationData.registration.certificateNumber, language)}</p>
                  </div>
                  <div>
                    <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.certificate.issuingAuthority')}</p>
                    <p className={`text-card-foreground ${isRTL ? 'text-right' : 'text-left'}`}>{registrationData.registration.ministry}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.certificate.issueDate')}</p>
                      <p className={`text-card-foreground date ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>{getLocalizedNumber(registrationData.registration.validityDate, language)}</p>
                    </div>
                    <div>
                      <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.certificate.expiryDate')}</p>
                      <p className={`text-card-foreground date ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>{getLocalizedNumber(registrationData.registration.expiryDate, language)}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6 bg-secondary/50 rounded-xl border border-border border-dashed">
                <div className="text-center" dir={direction}>
                  <motion.div
                    className="relative rounded-lg overflow-hidden mb-4 bg-muted/50 border-2 border-dashed border-muted-foreground/30"
                    style={{ minHeight: '200px' }}
                  >
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
                      <FileText className="h-16 w-16 text-muted-foreground/50 mb-4" />
                      <p className="text-muted-foreground font-medium text-lg">
                        {t('registration.certificate.comingSoon')}
                      </p>
                      <p className="text-muted-foreground/70 text-sm mt-2 text-center">
                        {registrationData.registration.ministry}
                      </p>
                    </div>
                  </motion.div>
                  {/* <p className="text-muted-foreground">{t('registration.certificate.displayedInOffice')}</p>
                  <p className="text-sm text-muted-foreground mt-2">{t('registration.certificate.availableOnWebsite')}</p> */}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Business & Staff */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            viewport={{ once: true }}
            className="bg-card border border-border rounded-2xl p-8"
            dir={direction}
          >
            <div className={`flex items-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="p-3 bg-primary/10 rounded-lg">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <h2 className="text-3xl font-bold text-card-foreground" dir={direction}>
                {t('registration.business.title')}
              </h2>
            </div>
            
            <div className="mb-8">
              <h3 className={`text-xl font-semibold text-card-foreground mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.business.businessInfo')}</h3>
              <div className="grid md:grid-cols-2 gap-6 p-6 bg-secondary/50 rounded-xl border border-border" dir={direction}>
                <div>
                  <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.business.legalName')}</p>
                  <p className={`text-card-foreground font-medium ${isRTL ? 'text-right' : 'text-left'}`}>{registrationData.business.legalName}</p>
                </div>
                <div>
                  <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.business.establishmentDate')}</p>
                  <p className={`text-card-foreground date ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>{getLocalizedNumber(registrationData.business.establishmentDate, language)}</p>
                </div>
                <div>
                  <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.business.registrationNumber')}</p>
                  <p className={`text-card-foreground font-medium phone ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>{getLocalizedNumber(registrationData.business.registrationNumber, language)}</p>
                </div>
                <div>
                  <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.business.registrationValidity')}</p>
                  <p className={`text-card-foreground date ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>{getLocalizedNumber(registrationData.business.registrationValidity, language)}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Business Details */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-card border border-border rounded-2xl p-8"
            dir={direction}
          >
            <div className={`flex items-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="p-3 bg-primary/10 rounded-lg">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <h2 className="text-3xl font-bold text-card-foreground" dir={direction}>
                {t('registration.business.businessInfo')}
              </h2>
            </div>
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-6 bg-secondary/50 rounded-xl border border-border" dir={direction}>
                  <h3 className={`font-semibold text-card-foreground mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.business.businessInfo')}</h3>
                  <div className="space-y-3">
                    <div>
                      <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.business.legalName')}</p>
                      <p className={`text-card-foreground font-medium ${isRTL ? 'text-right' : 'text-left'}`}>{registrationData.business.legalName}</p>
                    </div>
                    <div>
                      <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.business.establishmentDate')}</p>
                      <p className={`text-card-foreground date ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>{getLocalizedNumber(registrationData.business.establishmentDate, language)}</p>
                    </div>
                    <div>
                      <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.business.registrationNumber')}</p>
                      <p className={`text-card-foreground font-medium phone ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>{getLocalizedNumber(registrationData.business.registrationNumber, language)}</p>
                    </div>
                    <div>
                      <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.business.validityDate')}</p>
                      <p className={`text-card-foreground date ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>{getLocalizedNumber(registrationData.business.registrationValidity, language)}</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 bg-secondary/50 rounded-xl border border-border" dir={direction}>
                  <h3 className={`font-semibold text-card-foreground mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.business.contactInfo')}</h3>
                  <div className="space-y-3">
                    <div>
                      <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.business.officeAddress')}</p>
                      <p className={`text-card-foreground ${isRTL ? 'text-right' : 'text-left'}`}>{getLocalizedText(registrationData.business.officeAddress, language)}</p>
                    </div>
                    <div>
                      <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.business.website')}</p>
                      <a 
                        href={registrationData.business.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={`text-primary hover:underline ${isRTL ? 'text-right' : 'text-left'} block`}
                      >
                        {registrationData.business.website}
                      </a>
                    </div>
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.business.phone')}</p>
                      <PhoneNumberDisplay phone={registrationData.business.phone} />
                    </div>
                    <div>
                      <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.business.email')}</p>
                      <p className={`text-card-foreground ${isRTL ? 'text-right' : 'text-left'}`}>{registrationData.business.email}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6 bg-secondary/50 rounded-xl border border-border" dir={direction}>
                <h3 className={`font-semibold text-card-foreground mb-4 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''} ${isRTL ? 'text-right' : 'text-left'}`}>
                  <CreditCard className="h-5 w-5 text-primary" />
                  {t('registration.business.bankAccounts')}
                </h3>
                <div className="space-y-4">
                  {registrationData.business.bankAccounts.map((account, index) => (
                    <div key={index} className="p-4 bg-background/50 rounded-lg">
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.business.bankName')}</p>
                          <p className={`text-card-foreground font-medium ${isRTL ? 'text-right' : 'text-left'}`}>{getLocalizedText(account.bank, language)}</p>
                        </div>
                        <div>
                          <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.business.accountNumber')}</p>
                          <p className={`text-card-foreground font-medium phone ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>{getLocalizedNumber(account.accountNumber, language)}</p>
                        </div>
                      </div>
                      {(account.title || account.iban) && (
                        <div className="grid md:grid-cols-2 gap-4 pt-4 border-t border-border">
                          {account.title && (
                            <div>
                              <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.business.accountTitle')}</p>
                              <p className={`text-card-foreground font-medium ${isRTL ? 'text-right' : 'text-left'}`}>{getLocalizedText(account.title, language)}</p>
                            </div>
                          )}
                          {account.iban && (
                            <div>
                              <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.business.iban')}</p>
                              <p className={`text-card-foreground font-medium phone ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>{getLocalizedNumber(account.iban, language)}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Clearance Certificate */}
          {false && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="bg-card border border-border rounded-2xl p-8"
            dir={direction}
          >
            <div className={`flex items-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="p-3 bg-primary/10 rounded-lg">
                <Award className="h-6 w-6 text-primary" />
              </div>
              <h2 className="text-3xl font-bold text-card-foreground" dir={direction}>
                {t('registration.clearance.title')}
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 bg-secondary/50 rounded-xl border border-border" dir={direction}>
                <h3 className={`font-semibold text-card-foreground mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.clearance.details')}</h3>
                <div className="space-y-3">
                  <div>
                    <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.clearance.hostCountry')}</p>
                    <p className={`text-card-foreground font-medium text-lg mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>{getLocalizedText(registrationData.clearance.hostCountry, language)}</p>
                  </div>
                  <div>
                    <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.clearance.certificateNumber')}</p>
                    <p className={`text-card-foreground font-medium phone ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>{getLocalizedNumber(registrationData.clearance.certificateNumber, language)}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.clearance.issueDate')}</p>
                      <p className={`text-card-foreground date ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>{getLocalizedNumber(registrationData.clearance.issueDate, language)}</p>
                    </div>
                    <div>
                      <p className={`text-sm text-muted-foreground mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>{t('registration.clearance.expiryDate')}</p>
                      <p className={`text-card-foreground date ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>{getLocalizedNumber(registrationData.clearance.expiryDate, language)}</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${isRTL ? 'flex-row-reverse' : ''} ${
                      registrationData.clearance.status === 'Valid' 
                        ? 'bg-primary/20 text-primary' 
                        : 'bg-red-500/20 text-red-600'
                    }`}>
                      <div className={`w-2 h-2 rounded-full ${
                        registrationData.clearance.status === 'Valid' ? 'bg-primary' : 'bg-red-600'
                      }`} />
                      <span className="font-semibold">{t(`registration.clearance.${registrationData.clearance.status.toLowerCase()}`)}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6 bg-secondary/50 rounded-xl border border-border border-dashed">
                <div className="text-center" dir={direction}>
                  <motion.div
                    className="relative rounded-lg overflow-hidden mb-4 bg-muted/50 border-2 border-dashed border-muted-foreground/30"
                    style={{ minHeight: '200px' }}
                  >
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
                      <FileText className="h-16 w-16 text-muted-foreground/50 mb-4" />
                      <p className="text-muted-foreground font-medium text-lg">
                        {t('registration.certificate.comingSoon')}
                      </p>
                    </div>
                  </motion.div>
                  {/* <p className="text-muted-foreground">{t('registration.clearance.displayedInOffice')}</p>
                  <p className="text-sm text-muted-foreground mt-2">{t('registration.clearance.availableOnWebsite')}</p> */}
                </div>
              </div>
            </div>
          </motion.div>
          )}

          {/* Other Certificates */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="bg-card border border-border rounded-2xl p-8"
            dir={direction}
          >
            <div className={`flex items-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="p-3 bg-primary/10 rounded-lg">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <h2 className="text-3xl font-bold text-card-foreground" dir={direction}>
                {t('registration.otherCertificates.title')}
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {/* NTN Certificate */}
              <div className="p-6 bg-secondary/50 rounded-xl border border-border">
                <div className="text-center" dir={direction}>
                  <h3 className={`font-semibold text-card-foreground mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('registration.otherCertificates.ntn')}
                  </h3>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="relative group cursor-pointer rounded-lg overflow-hidden mb-4"
                    onClick={() => setSelectedOtherCertificateImage('/data/certificates/ntn.png')}
                  >
                    <img
                      src="/data/certificates/ntn.png"
                      alt="NTN Certificate"
                      className="w-full h-auto rounded-lg shadow-lg group-hover:opacity-90 transition-opacity"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-3 bg-black/50 backdrop-blur-sm rounded-full">
                        <ZoomIn className="h-8 w-8 text-white" />
                      </div>
                    </div>
                  </motion.div>
                  <p className="text-muted-foreground text-sm">{t('registration.otherCertificates.displayedInOffice')}</p>
                </div>
              </div>
              {/* Department of Tourist Service Certificate */}
              <div className="p-6 bg-secondary/50 rounded-xl border border-border">
                <div className="text-center" dir={direction}>
                  <h3 className={`font-semibold text-card-foreground mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('registration.otherCertificates.deptTouristService')}
                  </h3>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="relative group cursor-pointer rounded-lg overflow-hidden mb-4"
                    onClick={() => setSelectedOtherCertificateImage('/data/certificates/dept-tourist-service.jpeg')}
                  >
                    <img
                      src="/data/certificates/dept-tourist-service.jpeg"
                      alt="Department of Tourist Service Certificate"
                      className="w-full h-auto rounded-lg shadow-lg group-hover:opacity-90 transition-opacity"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-3 bg-black/50 backdrop-blur-sm rounded-full">
                        <ZoomIn className="h-8 w-8 text-white" />
                      </div>
                    </div>
                  </motion.div>
                  <p className="text-muted-foreground text-sm">{t('registration.otherCertificates.displayedInOffice')}</p>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </section>
      
      {/* Full Screen Image Modal */}
      <FullScreenImageModal
        selectedImage={selectedCertificateImage}
        tourTitle={t('registration.certificate.title')}
        onClose={() => setSelectedCertificateImage(null)}
      />
      {false && (
      <FullScreenImageModal
        selectedImage={selectedClearanceImage}
        tourTitle={t('registration.clearance.title')}
        onClose={() => setSelectedClearanceImage(null)}
      />
      )}
      <FullScreenImageModal
        selectedImage={selectedOtherCertificateImage}
        tourTitle={t('registration.otherCertificates.title')}
        onClose={() => setSelectedOtherCertificateImage(null)}
      />
    </div>
  )
}

